import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './faq.css';
import useTheme from '../ColorConfig/usetheme';

const FAQ = () => {
  const colors = useTheme();
  const [activeIndex, setActiveIndex] = useState(null);
  const [isInView, setIsInView] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sectionRef = useRef(null);
  
  // Fetch FAQ data from API
  useEffect(() => {
    const fetchFaqData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.dremerz.net/api/DZ_8923_Idlbay_Faq_FaqList');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setFaqItems(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching FAQ data:', err);
        setError('Failed to load FAQs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFaqData();
  }, []);
  
  // Set up Intersection Observer to detect when section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 } // Trigger when 30% of section is visible
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  
  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const toggleShowAll = () => {
    setShowAll(!showAll);
    // Close any open accordion when toggling show more/less
    setActiveIndex(null);
  };

  // Display only first 4 items by default, all items when showAll is true
  const displayedItems = showAll ? faqItems : faqItems.slice(0, 4);

  return (
    <div className="idlbay-faq-wrapper" ref={sectionRef}>
      <div className="idlbay-faq-section">
        {/* Title and Subtitle - Coming from BOTTOM */}
        <motion.div
          className="idlbay-title-container"
          initial={{ opacity: 0, y: 80 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: "easeOut" }} 
        >
          <motion.h1
            className="idlbay-faq-title"
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }} 
          >
            FAQ
          </motion.h1>

          <motion.h2 
            className="idlbay-faq-subtitle"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }} 
          >
            Find answers to common questions about our services
          </motion.h2>
        </motion.div>

        {/* Loading state */}
        {loading && (
          <div className="idlbay-faq-loading">
            <p>Loading FAQs...</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="idlbay-faq-error">
            <p>{error}</p>
          </div>
        )}

        {/* FAQ Items - Coming from BOTTOM */}
        {!loading && (
          <motion.div 
            className="idlbay-faq-container"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <AnimatePresence mode="popLayout">
              {displayedItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  className={`idlbay-faq-item ${activeIndex === index ? 'idlbay-active' : ''}`}
                  initial={{ opacity: 0, y: 70, scale: 0.95 }}
                  animate={isInView ? { 
                    opacity: 1, 
                    y: 0, 
                    scale: 1,
                    transition: {
                      duration: 0.6,
                      delay: 0.3 + (index * 0.1),
                      ease: "easeOut"
                    }
                  } : {}}
                  whileHover={{ 
                    y: -3,
                    transition: { duration: 0.15 }
                  }}
                  exit={{ 
                    opacity: 0, 
                    y: 50,
                    transition: { duration: 0.2 }
                  }}
                  layout
                >
                  <motion.button
                    className="idlbay-faq-question"
                    onClick={() => toggleAccordion(index)}
                    aria-expanded={activeIndex === index}
                    aria-controls={`idlbay-faq-answer-${item.id}`}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="idlbay-question-text">{item.faqQuestion}</span>
                    <motion.div
                      className="idlbay-faq-indicator"
                      animate={{ rotate: activeIndex === index ? 180 : 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          d="M6 9L12 15L18 9" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                        />
                      </svg>
                    </motion.div>
                  </motion.button>

                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        id={`idlbay-faq-answer-${item.id}`}
                        className="idlbay-faq-answer"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ 
                          opacity: 1, 
                          height: "auto",
                          transition: {
                            opacity: { duration: 0.2, delay: 0.05 },
                            height: { duration: 0.25 }
                          }
                        }}
                        exit={{ 
                          opacity: 0, 
                          height: 0,
                          transition: {
                            opacity: { duration: 0.15 },
                            height: { duration: 0.2 }
                          }
                        }}
                      >
                        <div className="idlbay-answer-content">
                          <p>{item.faqAnswer}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Show More/Less Button - Only show if there are more than 4 items */}
            {faqItems.length > 4 && (
              <motion.div 
                className="idlbay-show-more-container"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { 
                  opacity: 1, 
                  y: 0,
                  transition: { duration: 0.4, delay: 0.7 }
                } : {}}
              >
                <motion.button
                  className="idlbay-show-more-btn"
                  onClick={toggleShowAll}
                  whileHover={{ 
                    scale: 1.05,
                    backgroundColor: `var(--color-primary)`
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  {showAll ? 'Show Less' : 'Show More'}
                </motion.button>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FAQ;